import { z } from "zod";
import { userRoleSchema } from "./auth.js";
import { journeyStepStateSchema } from "./steps.js";
import { journeyTrackSchema } from "./journey.js";
import { consentKindSchema, lifeStageSchema, sexSchema } from "./onboarding.js";
// Mirrors @ferrivia/db's authMethodSchema — api-contract has no dependency
// on @ferrivia/db (it's shared with browser code), so the enum is
// duplicated here rather than imported.
export const authMethodSchema = z.enum(["ftn", "google", "fake", "email"]);
export const patientSummarySchema = z.object({
    id: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    email: z.string().nullable(),
    createdAt: z.string(),
    // Completeness of the two infusion-track steps the physician needs before
    // the Sydänkeskus appointment, never eligibility (see journeyStepStateSchema).
    // Set only on an infusion-filtered page — optional + nullable so an
    // unfiltered queue page is byte-for-byte what it is today.
    infusionSteps: z
        .object({
        labs: journeyStepStateSchema,
        screener: journeyStepStateSchema,
    })
        .nullable()
        .optional(),
});
export const patientDetailSchema = patientSummarySchema.extend({
    role: userRoleSchema,
    phone: z.string().nullable(),
    authMethod: authMethodSchema.nullable(),
    identityVerifiedAt: z.string().nullable(),
});
export const patientListQuerySchema = z.object({
    limit: z.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
    track: journeyTrackSchema.optional(),
});
export const patientListResponseSchema = z.object({
    items: z.array(patientSummarySchema),
    nextCursor: z.string().nullable(),
});
export const patientRelationshipResponseSchema = z.object({
    establishedAt: z.string(),
});
// One row per (kind), latest recorded — consents is append-only, so this is
// "what does the record say today", not the full history.
export const patientConsentSchema = z.object({
    kind: consentKindSchema,
    granted: z.boolean(),
    documentVersion: z.string(),
    recordedAt: z.string(),
});
// The clinic's Details tab. Identity (givenName/familyName/authMethod/
// identityVerifiedAt) is eID-verified and read-only; preferredName, email
// and phone are the only staff-editable fields. profile/consents are the
// customer's own onboarding answers, rendered as answered — "the software
// does not interpret" binds this surface, so no derived flags here.
export const patientDetailsSchema = z.object({
    id: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    preferredName: z.string().nullable(),
    authMethod: authMethodSchema.nullable(),
    identityVerifiedAt: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    profile: z
        .object({
        birthYear: z.number().int().nullable(),
        sex: sexSchema.nullable(),
        country: z.string().nullable(),
        city: z.string().nullable(),
        region: z.string().nullable().optional(),
        lifeStages: z.array(lifeStageSchema),
        lifeStagesAnsweredAt: z.string().nullable(),
        completedAt: z.string().nullable(),
    })
        .nullable(),
    consents: z.array(patientConsentSchema),
    createdAt: z.string(),
});
// null clears the field — a legitimate staff correction (e.g. a wrong phone
// number). Unlike onboardingUpdateSchema, phone is not format-validated
// beyond length: Finnish and international formats vary too much to reject
// safely, and a rejection would block the very typo fix this exists for.
export const patientContactUpdateSchema = z
    .object({
    preferredName: z.string().trim().min(1).max(100).nullable(),
    email: z.string().trim().email().nullable(),
    phone: z.string().trim().min(1).max(40).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
